import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

/**
 * Tabs.
 *
 * Not browser-style tabs with borders and a notched active edge — a row of
 * quiet labels with one sliding indicator. The indicator is measured from the
 * active button rather than assumed from an index, so labels of different
 * widths, a wrapped row and a language change all still line up.
 *
 * Arrow keys move between tabs and Home/End jump to the ends, which is what a
 * tablist is expected to do and what makes a long row usable without a mouse.
 */

export interface TabDefinition<T extends string> {
  readonly id: T;
  readonly label: string;
  readonly icon?: LucideIcon;
  /** A service's mark instead of an icon. */
  readonly mark?: ReactNode;
  /** A dot after the label: the state of what the tab is about. */
  readonly dot?: 'healthy' | 'down' | 'pending' | 'unknown';
  /** A number beside the label — how many rows are behind this tab. */
  readonly count?: number;
  readonly disabled?: boolean;
}

export interface TabsProps<T extends string> {
  readonly tabs: readonly TabDefinition<T>[];
  readonly active: T;
  readonly onChange: (id: T) => void;
  readonly label: string;
  /** Sits at the right of the row: a search box, a button, a count. */
  readonly trailing?: ReactNode;
}

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
  label,
  trailing,
}: TabsProps<T>): ReactNode {
  const list = useRef<HTMLDivElement | null>(null);
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);

  const measure = useCallback(() => {
    const container = list.current;
    const current = container?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!container || !current) return;

    setIndicator({
      // Relative to the scrolled content, not the viewport, so the indicator
      // stays under its tab when the row scrolls sideways.
      left: current.offsetLeft,
      width: current.offsetWidth,
    });
  }, []);

  // Layout effect: measuring after paint makes the indicator visibly jump on
  // the first render of every tabbed page.
  useLayoutEffect(measure, [measure, active, tabs]);

  useEffect(() => {
    const container = list.current;
    if (!container || typeof ResizeObserver === 'undefined') return;

    // Fonts load late and containers resize; both move the tabs out from under
    // an indicator that was measured once.
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [measure]);

  const onKeyDown = tabKeys(tabs, active, onChange);

  return (
    <div className="tabs">
      <div
        ref={list}
        className="tabs-list"
        role="tablist"
        aria-label={label}
        onKeyDown={onKeyDown}
      >
        {tabs.map((tab) => {
          const selected = tab.id === active;
          const Icon = tab.icon;

          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`tab-${tab.id}`}
              aria-controls={`panel-${tab.id}`}
              aria-selected={selected}
              // Only the active tab is in the tab order; arrow keys move within.
              tabIndex={selected ? 0 : -1}
              disabled={tab.disabled ?? false}
              className="tabs-tab"
              onClick={() => onChange(tab.id)}
            >
              {tab.mark ?? (Icon ? <Icon size={15} aria-hidden="true" /> : null)}
              {tab.label}
              {tab.dot ? <span className={`tabs-dot tabs-dot-${tab.dot}`} aria-hidden="true" /> : null}
              {tab.count !== undefined ? <span className="tabs-count">{tab.count}</span> : null}
            </button>
          );
        })}

        {indicator ? (
          <span
            className="tabs-indicator"
            aria-hidden="true"
            style={{ transform: `translateX(${indicator.left}px)`, width: `${indicator.width}px` }}
          />
        ) : null}
      </div>

      {trailing ? <div className="tabs-trailing">{trailing}</div> : null}
    </div>
  );
}

/** Arrow keys move between a tablist's tabs, Home and End to its ends. */
function tabKeys<T extends string>(tabs: readonly TabDefinition<T>[], active: T, onChange: (id: T) => void) {
  return (event: React.KeyboardEvent): void => {
    const enabled = tabs.filter((tab) => !tab.disabled);
    const index = enabled.findIndex((tab) => tab.id === active);
    if (index === -1) return;

    const move = (next: number): void => {
      event.preventDefault();
      const tab = enabled[(next + enabled.length) % enabled.length];
      if (tab) onChange(tab.id);
    };

    if (event.key === 'ArrowLeft') move(index - 1);
    else if (event.key === 'ArrowRight') move(index + 1);
    else if (event.key === 'Home') move(0);
    else if (event.key === 'End') move(enabled.length - 1);
  };
}

/**
 * Tabs inside a tab: a segmented row, so it reads as part of the page it sits
 * on rather than a second row of pages. The same keyboard as `Tabs`; `prefix`
 * keeps its element ids apart from the page's own tabs.
 */
export function SubTabs<T extends string>({
  tabs,
  active,
  onChange,
  label,
  prefix,
}: Omit<TabsProps<T>, 'trailing'> & { readonly prefix: string }): ReactNode {
  return (
    <div className="subtabs" role="tablist" aria-label={label} onKeyDown={tabKeys(tabs, active, onChange)}>
      {tabs.map((tab) => {
        const selected = tab.id === active;
        const Icon = tab.icon;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`${prefix}tab-${tab.id}`}
            aria-controls={`${prefix}panel-${tab.id}`}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            disabled={tab.disabled ?? false}
            className="subtabs-tab"
            onClick={() => onChange(tab.id)}
          >
            {tab.mark ?? (Icon ? <Icon size={14} aria-hidden="true" /> : null)}
            {tab.label}
            {tab.count !== undefined ? <span className="tabs-count">{tab.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({
  id,
  active,
  prefix = '',
  children,
}: {
  readonly id: string;
  readonly active: string;
  /** The same as the tablist's, for tabs inside a tab. */
  readonly prefix?: string;
  readonly children: ReactNode;
}): ReactNode {
  if (id !== active) return null;

  return (
    <div role="tabpanel" id={`${prefix}panel-${id}`} aria-labelledby={`${prefix}tab-${id}`} className="tab-panel" tabIndex={0}>
      {children}
    </div>
  );
}
